import type {
  Communication,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { createReference } from '@seen/core/references'
import { type FhirValue, isOfType } from '@seen/core/utils'
import { cn } from '@seen/ui'
import { IconLink } from '@tabler/icons-react'
import type { JSX } from 'react'
import { CommentMention } from '#src/components/CommentMention.tsx'
import { OrderMention } from '#src/components/OrderMention.tsx'
import { useResource } from '#src/hooks/useResource.ts'

export function MentionResource({
  reference,
  className,
}: {
  reference: FhirValue
  className?: string
}): JSX.Element | null {
  if (isOfType(reference, 'ServiceRequest')) {
    return (
      <OrderMention
        order={reference as Reference<ServiceRequest>}
        className={className}
      />
    )
  }
  if (isOfType(reference, 'Communication')) {
    return (
      <CommentMention
        comment={reference as Reference<Communication>}
        className={className}
      />
    )
  }
  return <FallbackMention reference={reference} className={className} />
}

function FallbackMention({
  reference,
  className,
}: {
  reference: FhirValue
  className?: string
}): JSX.Element {
  const { data: resource } = useResource(reference)
  const fallbackLabel =
    'display' in reference
      ? (referenceDisplay(reference) ?? reference.reference ?? 'Resource')
      : 'Resource'
  const label = resource
    ? referenceDisplay(createReference(resource))
    : fallbackLabel
  return (
    <span
      className={cn(
        'line-clamp-1 select-none items-center gap-1 truncate whitespace-nowrap rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px align-middle',
        className
      )}
    >
      <IconLink className="size-3.5 text-foreground-secondary" />
      <span>{label}</span>
    </span>
  )
}
